module Meevo.Plugins.Register {
    "use strict";

    export class ProductUsageSection extends Meevo.Plugins.Register.RegisterSectionBase {
        protected selectedSaleLine: Model.SaleLine;
        productUsageService: Meevo.Model.IProductUsageService;
        suppressIsDirty: boolean;
        originalProductUsageService: Meevo.Model.IProductUsageService;
        newVariant: any;
        excludedVariants: any[];
        serviceProductUsage: any;
        calculatingUsageAmountCharge: boolean;

        public variantCriteria = {
            enableProductUsageTracking: true,
            includeNotForSale: true,
            explicitProductInventoryTypes: [
                Meevo.Model.ProductInventoryTypeEnum.ShopSupply,
                Meevo.Model.ProductInventoryTypeEnum.RetailOrShopSupply
            ],
        };
        get $translate() { return this.registerContext.$translate; }
        get dataService() { return this.registerContext.dataService; }
        get modalService() { return this.registerContext.modalService; }
        get busyService() { return this.registerContext.busyService; }

        constructor(registerContext: Meevo.Plugins.Register.RegisterContext) {
            super(RegisterSectionTypeEnum.ProductUsage, registerContext, "productUsage", Register.RegisterSectionCatTypeEnum.Sale);           
            this.calculatingUsageAmountCharge = false;
        }

        reset() {
            this.excludedVariants = [];
            this.newVariant = null;
            this.selectedSaleLine = null;
            this.registerContext.hideCheckoutButtons = true;
            this.suppressIsDirty = false;
            this.serviceProductUsage = null;
        }

        clear(suppressIsDirty: boolean = true) {
            this.suppressIsDirty = suppressIsDirty;
            this.gotoPreviousSection();
        }

        private gotoPreviousSection() {
            this.registerContext.selectSection(this.registerContext.previouslySelectedSection.sectionType);
        }

        initialize(selectedSaleLine: Model.SaleLine) {
            this.selectedSaleLine = selectedSaleLine;
            if (selectedSaleLine.item.productUsage) {
                this.productUsageService = angular.copy(selectedSaleLine.item.productUsage);
                this.originalProductUsageService = angular.copy(this.productUsageService);

                const request = {
                    productUsages: this.productUsageService.variants.map(variant => ({
                        serviceId: this.productUsageService.serviceId,
                        variantId: variant.variantId,
                        usageAmount: variant.usageAmount}))
                };
    
                this.getProductUsageCharge(request).then(usageCharges => {
                    if (this.compareUsageCharges(usageCharges)) {
                        this.modalService.alert('MESSAGE_PRODUCT_USAGE_CHARGE_CHANGED');
                    }
                });
                this.rebuildExcludedVariants();
            }
            this.registerContext.hideCheckoutButtons = true;
        }

        delete(saleLine) {
            if (this.selectedSaleLine === saleLine) {
                this.registerContext.selectSection(RegisterSectionTypeEnum.Services);
            }
        }

        private compareUsageCharges(usageCharges): boolean {
            for (const productUsage of usageCharges.productUsages) {
                const variant = this.productUsageService.variants.find(variant => variant.variantId === productUsage.priceRequest.variantId);

                if (!variant) {
                    continue;
                }

                if ((variant.usageCharge || 0) != (productUsage.usageCharge || 0)) {
                    return true;
                }
            }

            return false;
        }

        private isValid() {
            return _(this.productUsageService.variants).all(variant => variant.usageAmount > 0);
        }

        apply(isValid: boolean) {
            if (this.calculatingUsageAmountCharge) {
                return;
            }

            if (!isValid || !this.isValid()) {
                return this.modalService.alert(this.$translate.instant('MESSAGE_COMPLETE_ALL_FIELDS_THAT_REQUIRE_ATTENTION'));
            }

            this.productUsageService.hasFlatFeeProductUsage = this.productUsageService.variants
                .some(variant => variant.chargeType == Model.ProductVariantChargeTypeEnum.FlatFee);

            this.registerContext.hideCheckoutButtons = false;
            this.selectedSaleLine.item.productUsage = this.productUsageService;

            this.registerContext.recalculateSale()
                .finally(() => {
                    this.gotoPreviousSection();
                });
        }        

        getServiceName(): string {
            return this.selectedSaleLine
                ? `${this.selectedSaleLine.item.shortcut}: ${this.selectedSaleLine.item.displayName}`
                : ''; 
        }

        private getProductUsageCharge(request) {
            return this.dataService.customMethodData(`/api/productUsage/price/list`, "POST", request)
                .then(response => {
                    return response.data;
                });
        }

        private getServiceProductUsage(serviceId) {
            return this.serviceProductUsage ?
                this.registerContext.$q.resolve(this.serviceProductUsage) :
                this.dataService.customMethodData(`/api/service/${serviceId}/productusage`, "GET", {})
                    .then(response => {
                        this.serviceProductUsage = response.data;
                    });
        }

        private populateUsageCharges(usageCharges) {
            if (!this.productUsageService) {
                return;
            }

            for (const productUsage of usageCharges.productUsages) {
                const variant = this.productUsageService.variants.find(variant => variant.variantId === productUsage.priceRequest.variantId);

                if (!variant) {
                    continue;
                }

                variant.usageCharge = productUsage.usageCharge || 0;
            }
        }
        
        usageAmountChange(variant: Meevo.Model.IProductUsageVariant) {
            //comes in as a string which doesn't cause the required red border to show when changing to 0 so cast to a number
            variant.usageAmount = +variant.usageAmount;
            if (variant.usageAmount === variant.previousUsageAmount) {
                return;
            }

            this.calculatingUsageAmountCharge = true;

            variant.previousUsageAmount = variant.usageAmount;

            const request = {
                productUsages: [{
                    serviceId: this.productUsageService.serviceId,
                    variantId: variant.variantId,
                    usageAmount: variant.usageAmount,
                }],
            };

            return this.getProductUsageCharge(request)
                .then(usageCharges => this.populateUsageCharges(usageCharges))
                .finally(() => this.calculatingUsageAmountCharge = false);
        }

        getVariantDisplayName(variant: Meevo.Model.IProductUsageVariant): string {
            return `${variant.productSku}: ${variant.productDisplayName}${(variant.variantDisplayName ? ' - ' + variant.variantDisplayName : '')}`;
        }

        getUnitOfMeasure(unitOfMeasure: Meevo.Model.UnitEnum, productUsageAmountType: Meevo.Model.ProductUsageAmountTypeEnum): string {
            switch (productUsageAmountType) {
                case Meevo.Model.ProductUsageAmountTypeEnum.PartialAmount:
                    return this.$translate.instant("ENUM_LABEL_" + unitOfMeasure) + "(s)";
                case Meevo.Model.ProductUsageAmountTypeEnum.WholeProduct:
                    return this.$translate.instant("LABEL_PRODUCT_USAGE_WHOLE_PRODUCT");
            }
        }

        isDirty() {
            return !this.suppressIsDirty &&
                !this.registerContext.isRefundMode &&
                this.selectedSaleLine.item.productUsage &&
                this.getVariantJSON(this.selectedSaleLine.item.productUsage.variants) !== this.getVariantJSON(this.productUsageService.variants);
        }

        private getVariantJSON(variants: Meevo.Model.IProductUsageVariant[]) {
            return JSON.stringify(_(variants).chain()
                .map(variant => {
                    return { variantId: variant.variantId, usageAmount: variant.usageAmount }
                })
                .sortBy("variantId")
                .value());
        }

        productVariantChanged() {
            if (!this.newVariant) {
                return;
            }

            this.buildAdditionalVariant().then(variant => {
                this.productUsageService.variants.push(variant);
                this.productUsageService.variants = this.productUsageService.variants.sort((a, b) => a.productSku.toLowerCase().localeCompare(b.productSku.toLowerCase()));

                this.newVariant = null;

                this.rebuildExcludedVariants();
                this.usageAmountChange(variant);
            });
        }

        private buildAdditionalVariant() {
            const originalVariant = this.originalProductUsageService.variants.find((originalVariant) =>
                originalVariant.variantId == this.newVariant.id);

            if (originalVariant) {
                return this.registerContext.$q.resolve(angular.copy(originalVariant));
            } else {
                //Check if its in the service definition
                return this.getServiceProductUsage(this.selectedSaleLine.item.id).then(() => {
                    const serviceVariant = this.serviceProductUsage.serviceProductVariantUsageCardInfoList.find((v) => {
                        return v.variant_TId === this.newVariant.id;
                    });
                    return {
                        productId: this.newVariant.productId,
                        variantId: this.newVariant.id,
                        productSku: this.newVariant.sku,
                        productDisplayName: this.newVariant.displayName,
                        variantDisplayName: this.newVariant.variantDisplayName,
                        unitOfMeasure: this.newVariant.productUnitOfMeasureEnum,
                        productUsageAmountType: this.newVariant.productUsageType,
                        usageAmount: (serviceVariant || {}).defaultAmount || 0,
                        usageCharge: null,
                        chargeType: this.newVariant.chargeType
                    };
                });
            }
        }

        private rebuildExcludedVariants() {
            this.excludedVariants = [];

            for (const variant of this.productUsageService.variants) {
                this.excludedVariants.push({ id: variant.variantId });
            }
        }

        preProcessData(response, request) {
            if (request.pageNumber == 0 && response.length == 0) {
                this.modalService.alert('MESSAGE_PRODUCT_USAGE_NO_PRODUCTS', Components.Modal.ModalTranslationModes.Translate, true);
            }
        }

        isRevertVariantVisible(variant: Meevo.Model.IProductUsageVariant): boolean {
            return angular.isDefined(variant.defaultUsageAmount) && variant.defaultUsageAmount != variant.usageAmount;
        }

        revertVariant(variant: Meevo.Model.IProductUsageVariant) {
            variant.usageAmount = variant.defaultUsageAmount;

            this.usageAmountChange(variant);
        }

        removeVariant(variant: Meevo.Model.IProductUsageVariant) {
            this.productUsageService.variants = this.productUsageService.variants
                .filter((serviceVariant) => serviceVariant.variantId !== variant.variantId);

            this.rebuildExcludedVariants();
        }

        get isPUTPackageRedeemed(): boolean {
            const redeemedPUTPackages = this.registerContext.getRedeemedPUTPackages(this.selectedSaleLine);
            const hasRedeemedPUTPackages = redeemedPUTPackages.length > 0;
            return hasRedeemedPUTPackages;
        }
    }
}
